class OpenAIFormatter {
    static formatForDisplay(response) {
        let systemPrompt = "";
        if (response.messages && response.messages.length > 0) {
            const firstMsg = response.messages[0];
            if (firstMsg.role === 'system' || firstMsg.role === 'developer') {
                systemPrompt = firstMsg.content.replace(/(Formatting re-enabled\n\n)+/g, "Formatting re-enabled\n\n");
            }
        }
        
        if (!systemPrompt) {
            const currentDate = new Date().toLocaleDateString('en-GB');
            const defaultPrompt = `You are a helpful, friendly assistant designed to provide accurate, concise, and relevant responses. Today is ${currentDate}. Follow the user's request closely. Do not assume a specific domain or document context unless the user explicitly asks for it. For simple greetings, reply briefly and naturally. Mention sources or document names only when you actually used retrieved document context or when the user asks for citations.`;
            systemPrompt = response.conversation?.system_prompt || defaultPrompt;
            systemPrompt = systemPrompt.replace(/(Formatting re-enabled\n\n)+/g, "Formatting re-enabled\n\n");
        }
        return {
            messages: response.messages || [],
            systemPrompt: response.messages?.[0]?.content || (() => {
                const currentDate = new Date().toLocaleDateString('en-GB');
                return `You are a helpful, friendly assistant designed to provide accurate, concise, and relevant responses. Today is ${currentDate}. Follow the user's request closely. Do not assume a specific domain or document context unless the user explicitly asks for it. For simple greetings, reply briefly and naturally. Mention sources or document names only when you actually used retrieved document context or when the user asks for citations.`;
            })(),
            model: response.conversation?.model,
            temperature: response.conversation?.temperature
        };
    }

    static formatForModel(messages, systemPrompt, modelId) {

        let formattedSystemPrompt = systemPrompt;
        let systemRole = "system";
        
        if ((modelId === "o3-mini-2025-01-31" || modelId === "o1-2024-12-17")) {
            if (!systemPrompt.includes("Formatting re-enabled")) {
                formattedSystemPrompt = "Formatting re-enabled\n\n" + systemPrompt;
            }
            systemRole = "developer";
        }

        const userMessages = messages.filter(msg => 
            msg.role !== 'system' && msg.role !== 'developer'
        );

        const formattedMessages = [
            { role: systemRole, content: formattedSystemPrompt },
            ...userMessages
        ];

        return {
            messages: formattedMessages.map(msg => {
                if (msg.images && msg.images.length > 0) {
                    const content = [];
                    
                    if (msg.content && msg.content.trim()) {
                        content.push({
                            type: "input_text",
                            text: msg.content
                        });
                    }
                    
                    msg.images.forEach(base64Image => {
                        content.push({
                            type: "input_image",
                            image_url: `data:image/jpeg;base64,${base64Image}`
                        });
                    });

                    return {
                        role: msg.role,
                        content: content
                    };
                }

                return {
                    role: msg.role,
                    content: msg.content
                };
            }),
            system_prompt: null  
        };
    }

    static generateTitle(messages) {
        const firstUserMessage = messages.find(msg => msg.role === 'user');
        if (!firstUserMessage) return "New Chat";
        
        const content = firstUserMessage.content;
        if (typeof content === 'string') {
            return content.length > 20 ? content.substring(0, 20) + "..." : content;
        }
        if (Array.isArray(content)) {
            const textContent = content.find(item => item.type === 'text')?.text || '';
            return textContent.length > 20 ? textContent.substring(0, 20) + "..." : textContent;
        }
        return "New Chat";
    }
}

class PerplexityFormatter extends OpenAIFormatter {
    // Inherits all methods from OpenAIFormatter since they handle messages the same way
}

class AWSClaudeFormatter {
    static formatForDisplay(response) {
      
        
        const messages = response.messages.map(msg => {
            // Handle array content (images/text)
            if (Array.isArray(msg.content)) {
                const formattedContent = [];
                let hasImage = false;
    
                msg.content.forEach(item => {
                    if (item.type === 'text') {
                        formattedContent.push({
                            text: item.text,
                            type: 'text'
                        });
                    } else if (item.type === 'image') {
                        hasImage = true;
                        formattedContent.push({
                            type: 'image_url',
                            image_url: {
                                url: `data:${item.source.media_type};base64,${item.source.data}`
                            }
                        });
                    }
                });
    
                // If there are no images, return just the text content as a string
                if (!hasImage && formattedContent.length === 1) {
                    return {
                        role: msg.role,
                        content: formattedContent[0].text
                    };
                }
    
                // If there are images or multiple text items, return the array format
                return {
                    role: msg.role,
                    content: formattedContent
                };
            }
    
            // Handle simple text messages
            return {
                role: msg.role || 'user',
                content: msg.content || msg
            };
        });
    
        const currentDate = new Date().toLocaleDateString('en-GB');
        const defaultPrompt = `You are a helpful, friendly assistant designed to provide accurate, concise, and relevant responses. Today is ${currentDate}. Follow the user's request closely. Do not assume a specific domain or document context unless the user explicitly asks for it. For simple greetings, reply briefly and naturally. Mention sources or document names only when you actually used retrieved document context or when the user asks for citations.`;
        messages.unshift({
            role: 'system',
            content: response.conversation?.system_prompt || defaultPrompt
        });

        return {
            messages,
            systemPrompt: response.conversation?.system_prompt || defaultPrompt,
            model: response.conversation?.model,
            temperature: response.conversation?.temperature
        };
    }

    static formatForModel(messages, systemPrompt) {
        const formattedMessages = messages.filter(msg => msg.role !== 'system').map(msg => {
            // Handle messages with images array
            if (msg.images && msg.images.length > 0) {
                const content = [];
                
                if (msg.content && msg.content.trim()) {
                    content.push({
                        type: "text",
                        text: msg.content
                    });
                }
                
                msg.images.forEach(base64Image => {
                    content.push({
                        type: "image",
                        source: {
                            type: "base64",
                            media_type: "image/jpeg",
                            data: base64Image
                        }
                    });
                });
    
                return {
                    role: msg.role === 'user' ? 'user' : 'assistant',
                    content: content
                };
            }

            // Handle messages that already have content array
            if (Array.isArray(msg.content)) {
                return {
                    role: msg.role === 'user' ? 'user' : 'assistant',
                    content: msg.content.map(item => {
                        if (item.type === 'image_url') {
                            return {
                                type: 'image',
                                source: {
                                    type: 'base64',
                                    media_type: 'image/jpeg',
                                    data: item.image_url.url.split(',')[1]
                                }
                            };
                        }
                        return item;
                    })
                };
            }
    
            // Handle simple text messages
            return {
                role: msg.role === 'user' ? 'user' : 'assistant',
                content: [{
                    type: "text",
                    text: msg.content
                }]
            };
        });
    
        return {
            messages: formattedMessages,
            system_prompt: systemPrompt
        };
    }

    static generateTitle(messages) {
        const firstUserMessage = messages.find(msg => msg.role === 'user');
        if (!firstUserMessage) return "New Chat";
        
        const content = firstUserMessage.content;
        
        if (Array.isArray(content)) {
            const textContent = content.find(item => item.type === 'text')?.text || '';
            return textContent.length > 20 ? textContent.substring(0, 20) + "..." : textContent;
        }
        
        if (typeof content === 'string') {
            return content.length > 20 ? content.substring(0, 20) + "..." : content;
        }
        
        return "New Chat";
    }
}

 
let modelFormatters = {};


export const initializeFormatters = (models) => {
    modelFormatters = {};
    
    Object.values(models).forEach(provider => {
        provider.models.forEach(model => {
            const formatterName = model.model_formatter;
            let FormatterClass;
            switch (formatterName) {
                case 'OpenAIFormatter':
                    FormatterClass = OpenAIFormatter;
                    break;
                case 'PerplexityFormatter':
                    FormatterClass = PerplexityFormatter;
                    break;
                case 'AWSClaudeFormatter':
                    FormatterClass = AWSClaudeFormatter;
                    break;
                default:
                    FormatterClass = OpenAIFormatter; 
            }
            modelFormatters[model.id] = FormatterClass;
        });
    });
};

export const formatChatForDisplay = (response, modelId) => {
    const formatter = modelFormatters[modelId] || OpenAIFormatter;
    return formatter.formatForDisplay(response);
};

export const formatChatForModel = (messages, systemPrompt, modelId) => {
    const formatter = modelFormatters[modelId] || OpenAIFormatter;
    return formatter.formatForModel(messages, systemPrompt, modelId);
};

export const generateTitle = (messages, modelId) => {
    const formatter = modelFormatters[modelId] || OpenAIFormatter;
    return formatter.generateTitle(messages);
};

export function formatSystemPromptForModel(systemPrompt, modelId) {
    if (modelId === "o3-mini-2025-01-31") {
        return "Formatting re-enabled\n\n" + systemPrompt;
    }

    if (modelId === "o1-2024-12-17") {
        return "Formatting re-enabled\n\n" + systemPrompt;
    }
    
    return systemPrompt;
}
